<script>
	import { acceptedFileUploadList, holdingTransactionTypeList, projectSettings, exchangeList } from "../../config";
	import { pageNumber, model } from "../../stores";
	import { stringListToOptionList } from "../../components/lib/helpers";
	import { validator } from "../../validation";
	import { getTealiumID } from "../../tealium";

	import ListForm from "../../components/ListForm.svelte";
	import Holding from "../../models/Holding";
	import HoldingTransaction from "../../models/HoldingTransaction";
	import Input from "../../components/Input.svelte";
	import NumberInput from "../../components/NumberInput.svelte";
	import CurrencyInput from "../../components/CurrencyInput.svelte";
	import NavigationButtons from "../../components/NavigationButtons.svelte";
	import DropZone from "../../components/DropZone.svelte";
	import Datepicker from "../../components/Datepicker.svelte";
	import Select from "../../components/Select.svelte";
	import { tick } from "svelte";

	// props
	export let title = null;
	export let pageIndex = null;
	export let nextPage = null;
	export let previousPage = null;
	export let persistUrl = null;
	export let pageTitle = null;
	export let disabled = false;
	export let lastVisitedPage = null;

	// dropzone config
	let id = "fileUploader";
	let options = {
		url: "/api/file-upload?token=" + $model.RegistrationToken,
		acceptedFiles: acceptedFileUploadList
	};

	// component state updating
	if (!disabled) {
		$pageNumber = pageIndex;
		$model.LastPageVisited = lastVisitedPage;
		document.title = pageTitle || `${projectSettings.classActionName} - Review`;
		$model.TealiumId = $model.TealiumId || getTealiumID();
	}

	// list management
	if ($model.Holdings.length === 0) $model.Holdings = [new Holding()];

	let liveIndex = 0;
	$: liveHolding = $model.Holdings[liveIndex];

	const addHolding = () => {
		if ($validator.validateAll(document.querySelector("form")).length > 0) return;
		$model.Holdings = [...$model.Holdings, new Holding()];
		liveIndex = $model.Holdings.length - 1;
	};

	const removeHolding = (index) => {
		if (liveIndex === index) liveIndex = 0;
		else if (liveIndex > index) liveIndex -= 1;
		$model.Holdings = [...$model.Holdings.filter((_, i) => i !== index)];
	};

	const setLiveHolding = (index) => {
		if ($validator.validateAll(document.querySelector("form")).length > 0) return;

		liveIndex = index;
		let errors = $validator.clearAllVisualError(document.querySelector("form"));
		errors[0].scrollIntoView({ behavior: "smooth" });
	};

	const validHolding = (holding) => {
		return (
			holding.HoldingName &&
			holding.RegisteredOwner &&
			holding.HoldingTransactions.every((transaction) => {
				return (
					transaction.TransactionDate &&
					transaction.TransactionType &&
					transaction.Exchange &&
					transaction.NumberOfShares &&
					transaction.PricePerShare &&
					transaction.Brokerage
				);
			})
		);
	};

	const beforeSubmitAsync = async () => {
		// Check to see if any holdings that haven't been displayed are invalid
		for (var i = 0; i < $model.Holdings.length; i++) {
			if (i == liveIndex) continue;

			if (!validHolding($model.Holdings[i])) {
				liveIndex = i;

				await tick();

				let errors = $validator.validateAll(document.querySelector("form"));
				errors[0].scrollIntoView({ behavior: "smooth" });
				return false;
			}
		}

		return true;
	};

	// update model on name input binding
	$: if (liveHolding.HoldingName) {
		liveHolding = $model.Holdings[liveIndex];
	}
</script>

<section>
	{#if disabled}
		<div class="row">
			<div class="col-md-12">
				<h2>Holding and Transaction Details</h2>
				{#each $model.Holdings as holding}
					<div class="form-group">
						<h3>{holding.HoldingName}</h3>
						<Input
							bind:value={holding.HoldingName}
							label={"Name of Holding"}
							id={"HoldingName"}
							maxLength={"8000"}
							required
							{disabled}
						/>
						<Input
							bind:value={holding.RegisteredOwner}
							label={"Registered Owner"}
							id={"RegisteredOwner"}
							maxLength={"8000"}
							required
							{disabled}
						/>
						<Input
							bind:value={holding.HoldingIdentificationNumber}
							label={"SRN/HIN (Australian shareholder only, if known)"}
							id={"HoldingIdentificationNumber"}
							maxLength={"25"}
							{disabled}
						/>
						<div class="form-row">
							<div class="form-group col-md-12">
								<p>Holding Documents</p>
								{#if holding.documentList && holding.documentList.length > 0}
									<table class="table table-sm">
										<tr>
											<th>Document Name</th>
										</tr>
										{#each holding.documentList as file}
											<tr>
												<td>{file.fileName}</td>
											</tr>
										{/each}
									</table>
								{:else}
									<p>No documents uploaded</p>
								{/if}
							</div>
						</div>
					</div>
					<div class="form-group">
						<ListForm
							bind:bindList={holding.HoldingTransactions}
							listModel={HoldingTransaction}
							modelName={"Transaction"}
							subHeading={"Transactions"}
							questionText={null}
							{disabled}
							rowDefinition={[
								{
									rowTitle: "Transaction Date",
									rowWidth: "3",
									rowDataName: "TransactionDate",
									rowComponent: Datepicker,
									bindTarget: "formattedSelected",
									rowComponentProps: {
										start: new Date(2019, 0, 1),
										end: new Date(),
										placeholder: "e.g. 01/05/1987",
										openOnClass: "calendar-pop"
									}
								},
								{
									rowTitle: "Transaction Type",
									rowWidth: "2",
									rowDataName: "TransactionType",
									rowComponent: Select,
									rowComponentProps: {
										optionList: stringListToOptionList(holdingTransactionTypeList),
										textProperty: "name",
										valueProperty: "value"
									}
								},
								{
									rowTitle: "Exchange",
									rowWidth: "2",
									rowDataName: "Exchange",
									rowComponent: Select,
									rowComponentProps: {
										optionList: stringListToOptionList(exchangeList),
										textProperty: "name",
										valueProperty: "value"
									}
								},
								{
									rowTitle: "Number of Shares",
									rowWidth: "1",
									rowDataName: "NumberOfShares",
									rowComponent: NumberInput,
									rowComponentProps: {}
								},
								{
									rowTitle: "Price per Share",
									rowWidth: "2",
									rowDataName: "PricePerShare",
									rowComponent: CurrencyInput,
									rowComponentProps: {}
								},
								{
									rowTitle: "Brokerage",
									rowWidth: "2",
									rowDataName: "Brokerage",
									rowComponent: CurrencyInput,
									rowComponentProps: {}
								}
							]}
						/>
					</div>
				{/each}
			</div>
		</div>
	{:else}
		<div class="row panelWrapper">
			<div class="col-sm-3 listPanel">
				<h4>Account/Fund Name</h4>

				{#each $model.Holdings as holding, i}
					<div class="listPanelItem">
						<p class="listPanelItemName">
							{holding && holding.HoldingName ? holding.HoldingName : `Holding ${i + 1}`}
						</p>
						<span class="listPanelButtonWrapper">
							<button
								type="button"
								class="btn btn-outline-secondary btn-sm"
								style={`visibility: ${liveIndex === i ? "hidden" : "visible"}`}
								on:click|preventDefault={() => setLiveHolding(i)}>Edit</button
							>
							<button
								type="button"
								class="btn btn-outline-secondary btn-sm"
								style={`visibility: ${$model.Holdings.length === 1 ? "hidden" : "visible"}`}
								on:click|preventDefault={() => removeHolding(i)}>Remove</button
							>
						</span>
					</div>
				{/each}

				{#if !disabled}
					<button
						class="btn btn-primary btn-sm"
						style="margin-top: 5px"
						title={`Add Holding`}
						on:click|preventDefault={() => addHolding()}>Add Holding</button
					>
				{/if}
			</div>
			<div class="col-sm-9">
				<h1>Holding and Transaction Details</h1>
				<p>
					The following sections are required to be completed per holding, if you wish to submit multiple
					holdings you may do so by selecting "Add Holding".
				</p>
				<Input
					bind:value={liveHolding.HoldingName}
					label={"Name of Holding"}
					id={"HoldingName"}
					maxLength={"8000"}
					required
					{disabled}
				/>
				<Input
					bind:value={liveHolding.RegisteredOwner}
					label={"Registered Owner"}
					id={"RegisteredOwner"}
					maxLength={"8000"}
					required
					{disabled}
				/>
				<Input
					bind:value={liveHolding.HoldingIdentificationNumber}
					label={"SRN/HIN (Australian shareholder only, if known)"}
					id={"HoldingIdentificationNumber"}
					maxLength={"25"}
					{disabled}
				/>
				<p style="margin-top: 20px;">
					You can provide details of your transactions in EML shares in the period from 19 December 2020 to 25
					July 2022 by completing the details below (click on the “Add a transaction” button to add additional
					transaction lines if you had more than one purchase or sale). If you had multiple transactions you
					can provide your transaction details using the spreadsheet available <a
						href="/documents/EMLTransactionsSpreadsheet.xlsx"
						download>here</a
					> or upload your own transaction listing using the upload box below.
				</p>
				<ListForm
					bind:bindList={liveHolding.HoldingTransactions}
					listModel={HoldingTransaction}
					modelName={"Transaction"}
					questionText={"Transactions"}
					subHeading={"Stock Code EML"}
					{disabled}
					rowDefinition={[
						{
							rowTitle: "Transaction Date",
							rowWidth: "3",
							rowDataName: "TransactionDate",
							rowComponent: Datepicker,
							bindTarget: "formattedSelected",
							rowComponentProps: {
								start: new Date(2020, 11, 19),
								end: new Date(2022, 6, 25),
								placeholder: "e.g. 01/05/2021",
								openOnClass: "calendar-pop",
								required: true
							}
						},
						{
							rowTitle: "Transaction Type",
							rowWidth: "2",
							rowDataName: "TransactionType",
							rowComponent: Select,
							rowComponentProps: {
								optionList: stringListToOptionList(holdingTransactionTypeList),
								textProperty: "name",
								valueProperty: "value",
								required: true
							}
						},
						{
							rowTitle: "Exchange",
							rowWidth: "2",
							rowDataName: "Exchange",
							rowComponent: Select,
							rowComponentProps: {
								optionList: stringListToOptionList(exchangeList),
								textProperty: "name",
								valueProperty: "value",
								required: true
							}
						},
						{
							rowTitle: "No. of Shares",
							rowWidth: "1",
							rowDataName: "NumberOfShares",
							rowComponent: NumberInput,
							rowComponentProps: {
								required: true
							}
						},
						{
							rowTitle: "Price per Share",
							rowWidth: "2",
							rowDataName: "PricePerShare",
							rowComponent: CurrencyInput,
							rowComponentProps: {
								required: true,
								placeholder: "0.00"
							}
						},
						{
							rowTitle: "Brokerage",
							rowWidth: "2",
							rowDataName: "Brokerage",
							rowComponent: CurrencyInput,
							rowComponentProps: {
								required: true,
								placeholder: "0.00"
							}
						}
					]}
				/>
				<div class="form-row">
					<div class="form-group col-md-12">
						<DropZone
							{options}
							{id}
							message="Drop files here or click to upload for this holding"
							bind:documentList={liveHolding.documentList}
						></DropZone>
					</div>
				</div>
			</div>
		</div>
	{/if}

	{#if !disabled}
		<div class="form-group form-row">
			<div class="col-md-12 last">
				<NavigationButtons
					{beforeSubmitAsync}
					{persistUrl}
					{previousPage}
					{nextPage}
					tealiumTag={true}
					pageName={title}
				></NavigationButtons>
			</div>
		</div>
	{/if}
</section>

<style>
	.listPanel {
		padding-top: 5px;
	}

	.listPanelItem {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 2px;
	}

	.listPanelItemName {
		margin: 0;
		text-overflow: ellipsis;
		overflow: hidden;
		padding-right: 10px;
		white-space: nowrap;
	}

	.listPanelButtonWrapper {
		white-space: nowrap;
	}

	@media only screen and (max-width: 991px) {
		.panelWrapper {
			flex-direction: column;
		}
		.listPanel {
			padding-top: 0;
			max-width: unset;
			margin-bottom: 10px;
		}
	}
</style>
